import { IComponentOptions, IPromise, IQService } from "angular";
import { AutomationTriggerType, IAutomation } from "@gtmhub/automation";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IEmployee } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { IInsightboard } from "@gtmhub/insightboards";
import { IInsight } from "@gtmhub/insights";
import { IKpi } from "@gtmhub/kpis";
import { IList } from "@gtmhub/lists/models/models";
import { IRole } from "@gtmhub/roles";
import { SearchService } from "@gtmhub/search/services/search.service";
import { ITeam } from "@gtmhub/teams";
import { UserService } from "@gtmhub/users";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { ISearchParams } from "@webapp/search/models/search-api.models";
import { Search, SearchCollection } from "@webapp/search/models/search.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { Task } from "@webapp/tasks/models/tasks.models";
import { GlobalSearchItem } from "../../models/global-search";
import { GlobalSearchItemEnriched } from "../../models/global-search-item";
import { GlobalSearchService } from "../../services/global-search-service";
import { searchItemsToEnrichedGlobalSearchItemCollection } from "../../utils/global-search";

interface IStateParams {
  searchTerm: string;
}

type SearchMode = "open" | "closed";

export class SimpleGlobalSearchCtrl {
  okrs: Metric & IGoal[];
  sessions: Session[];
  tasks: Task[];
  employees: IEmployee[];
  teams: ITeam[];
  lists: IList[];
  insights: IInsight[];
  insightboards: IInsightboard[];
  kpis: IKpi[];
  automations: IAutomation<AutomationTriggerType>[];
  roles: IRole[];
  allItems: GlobalSearchItemEnriched<SearchCollection>[] = [];

  searchTerm: string;
  itemsPerCollection: number;
  indicators: {
    gettingSearchData: boolean;
  };

  mode: SearchMode = "closed";
  permission: "ManageGoals" | "ManageWhiteboards";
  stateView = false;
  private initialized = false;

  // ---- Bindings ---- //
  searchCollections: string[];
  totalNumberOfItems: number;
  checkPermissions: boolean;
  searchPlaceholder: string;
  activeUsersOnly: boolean;
  activeTeamsOnly: boolean;
  onChange: ({ item }: { item: Search<SearchCollection> }) => unknown;

  public static $inject = ["$q", "$stateParams", "SearchService", "GlobalSearchService", "UIErrorHandlingService", "$rootScope", "UserService"];

  constructor(
    private $q: IQService,
    private $stateParams: IStateParams,
    private searchService: SearchService,
    private globalSearchService: GlobalSearchService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $rootScope: ITraceRootScopeService,
    private userService: UserService
  ) {
    this.indicators = {
      gettingSearchData: false,
    };
  }

  initSearch(): void {
    if (!this.initialized) {
      this.initialized = true;
      this.stateView = true;
      this.searchTerm = this.$stateParams.searchTerm;

      if (this.searchTerm) {
        this.getSearchData(this.searchTerm);
      }
    }
  }

  select(selectedItem: Search<SearchCollection>): void {
    this.onChange({ item: selectedItem });
    this.searchTerm = "";
  }

  onSearchTermChange(searchTerm: string): void {
    this.$rootScope.traceAction("ss__changed_search_term", () => {
      if (searchTerm) {
        this.getSearchData(searchTerm);
      } else {
        this.changeMode("closed");
      }
    });
  }

  closeSearchMenu(event: JQueryEventObject): void {
    // Do not close the element if the user clicked on part of the search body
    const searchClasses = ["search-actions", "search-field"];

    if (!searchClasses.some((c) => event.target.classList.contains(c))) {
      this.changeMode("closed");
      this.allItems = [];
    }
  }

  onItemHover($event: JQueryEventObject, { hovered }: { hovered: boolean }): void {
    const selectedEl = document.querySelector(".selected-item");
    if (selectedEl && selectedEl.classList.contains("item")) {
      selectedEl.classList.remove("selected-item");
    }

    if (hovered) {
      $event.target.classList.add("selected-item");
    } else {
      $event.target.classList.remove("selected-item");
    }
  }

  public hasManagePermission = (item): boolean => {
    if (!item.permissions || !this.permission) {
      return true;
    }

    return item.permissions.indexOf(this.permission) > -1;
  };

  private getSearchData(searchTerm: string): void {
    this.indicators.gettingSearchData = true;

    const queryParams: ISearchParams = {
      skip: 0,
      take: this.totalNumberOfItems,
    };

    this.getCollections(searchTerm, queryParams);
  }

  private getCollections(searchTerm: string, queryParams: ISearchParams): void {
    const initializationPromises: IPromise<unknown>[] = this.searchCollections.map((collection) => this.getOneOfAll(collection, searchTerm, queryParams));

    this.$q.all(initializationPromises).then(
      () => {
        this.itemsPerCollection = this.totalNumberOfItems / this.searchCollections.filter((collection) => this[collection] && this[collection].length).length;
        this.allItems = [];

        this.searchCollections
          .filter((collection) => this[collection] && this[collection].length)
          .forEach((collection) => this.allItems.push(...this[collection].slice(0, this.itemsPerCollection)));

        this.indicators.gettingSearchData = false;
        this.allItems.sort((a, b) => b.score - a.score);

        if (this.allItems && this.allItems.length) {
          this.changeMode("open");
        } else {
          this.changeMode("closed");
        }
      },
      (err) => {
        this.indicators.gettingSearchData = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  }

  private getOneOfAll(collectionName: string, searchTerm: string, queryParams: ISearchParams) {
    const searchBody = this.globalSearchService.getSearchBody(searchTerm, collectionName, {
      activeUsersOnly: this.activeUsersOnly,
      activeTeamsOnly: this.activeTeamsOnly,
    });
    return this.searchService.getSomeSearchData<GlobalSearchItem<SearchCollection>>(searchBody, queryParams).then(
      (searchResults) => {
        if (collectionName === "employees") {
          const userIds = searchResults.items.map((item) => item.id);
          const userReqParams = { filter: { _id: { $in: userIds } }, page: 1, fields: "id,permissions" };

          return this.userService.getUsersV2(userReqParams).then((users) => {
            this.employees = searchItemsToEnrichedGlobalSearchItemCollection(collectionName, searchResults.items).map((item) => {
              const user = users.items.find((user) => user.id === item.id);
              if (user) {
                return { ...item, permissions: user.permissions };
              }

              return item;
            });
          });
        }

        this[collectionName] = searchItemsToEnrichedGlobalSearchItemCollection(collectionName, searchResults.items);
      },
      (err) => this.$q.reject(err)
    );
  }

  private changeMode(mode: "open" | "closed"): void {
    this.mode = mode;
  }
}

export const SimpleGlobalSearchComponent: IComponentOptions = {
  template: require("./simple-global-search.html"),
  controller: SimpleGlobalSearchCtrl,
  bindings: {
    permission: "<",
    searchCollections: "<",
    totalNumberOfItems: "<",
    searchPlaceholder: "@",
    activeUsersOnly: "<",
    activeTeamsOnly: "<",
    onChange: "&",
  },
};
